import { http } from '@/network/http';
import type {
  PayrollResponse,
  TherapistDetailSummary,
  TherapistInfo,
  PendingReport,
  AdminStats,
  ApprovalConfig,
  ApprovalConfigFormData,
  HospitalReportsResponse,
  Role,
  RoleListResponse,
  ActiveRolesResponse,
  MyPermissionsResponse,
  RoleFormData,
  EmploymentRole,
  EmploymentRoleFormData,
  EmploymentRoleListResponse,
  EmploymentRoleRate,
  EmploymentRoleRateFormData,
  EmploymentRoleHospitalBonus,
  EmploymentRoleHospitalBonusFormData,
  EmploymentRoleAllowance,
  EmploymentRoleAllowanceFormData,
  EmploymentRoleHospitalSubset,
  SupervisionBonusConfig,
  SupervisionBonusFormData,
  AllowanceType,
  AllowanceTypeFormData,
  AllowanceTypeListResponse,
  Currency,
  CurrencyFormData,
  CurrencyListResponse,
  ExportPreset,
  ExportPresetFormData,
} from './types';

export const adminApi = {
  getStats: (month?: string, status?: string) =>
    http.get<{ success: boolean; stats: AdminStats }>('/dashboard/admin/stats', {
      params: { ...(month && { month }), ...(status && { status }) },
    }),

  getPayroll: (filters: { month?: string; startMonth?: string; endMonth?: string; hospitalId?: string; name?: string; role?: string } = {}) =>
    http.get<{ success: boolean } & PayrollResponse>('/admin/payroll', {
      params: filters,
    }),

  getTherapistDetail: (therapistId: string, params?: { month?: string; startMonth?: string; endMonth?: string }) =>
    http.get<{
      success: boolean;
      therapist: TherapistInfo & { phone: string | null };
      month: string;
      reports: any[];
      allowances: Array<{ id: string; name: string; nameEn: string | null; code: string; amount: number; period: string }>;
      summary: TherapistDetailSummary;
    }>(`/admin/payroll/${therapistId}`, {
      params,
    }),

  getPendingReports: (page?: number, limit?: number) =>
    http.get<{ success: boolean; reports: PendingReport[]; total: number }>('/admin/pending-reports', {
      params: { page, limit },
    }),

  getHospitalReports: (params: { month?: string; startMonth?: string; endMonth?: string; reportTypeCode?: string; status?: string } = {}) =>
    http.get<HospitalReportsResponse>('/admin/hospital-reports', { params }),

  approveReport: (reportType: string, id: string) =>
    http.post(`/admin/approve/${reportType}/${id}`),

  rejectReport: (reportType: string, id: string, reason: string) =>
    http.post(`/admin/reject/${reportType}/${id}`, { reason }),

  rePending: (reportType: string, id: string) =>
    http.post(`/admin/re-pending/${reportType}/${id}`),

  bulkApprove: (reportIds: Array<{ id: string; reportType: string }>) =>
    http.post<{ success: boolean; approvedCount: number }>('/admin/bulk-approve', { reportIds }),

  bulkReject: (reportIds: Array<{ id: string; reportType: string }>, reason: string) =>
    http.post<{ success: boolean; rejectedCount: number }>('/admin/bulk-reject', { reportIds, reason }),

  markPaid: (reportType: string, id: string) =>
    http.post(`/admin/mark-paid/${reportType}/${id}`),

  bulkMarkPaid: (reportIds: Array<{ id: string; reportType: string }>) =>
    http.post<{ success: boolean; paidCount: number }>('/admin/bulk-mark-paid', { reportIds }),

  emailPayrollPdf: (therapistId: string, pdfBase64: string) =>
    http.post<{ success: boolean; emailResult: { success: boolean; messageId?: string } }>('/admin/payroll/email-pdf', { therapistId, pdfBase64 }),

  getApprovalConfigs: () =>
    http.get<{ success: boolean; configs: ApprovalConfig[] }>('/approvals/config'),

  createApprovalConfig: (data: ApprovalConfigFormData) =>
    http.post<{ success: boolean; config: ApprovalConfig }>('/approvals/config', data),

  updateApprovalConfig: (id: string, data: { requiresApproval: boolean }) =>
    http.put<{ success: boolean; config: ApprovalConfig }>(`/approvals/config/${id}`, data),

  deleteApprovalConfig: (id: string) =>
    http.delete(`/approvals/config/${id}`),

  // Roles
  getRoles: (params?: { page?: number; limit?: number; search?: string; isActive?: string }) =>
    http.get<RoleListResponse>('/roles', { params }),

  getAvailablePermissions: () =>
    http.get<{ success: boolean; tab: string[]; action: string[]; dataScope: string[] }>('/roles/available-permissions'),

  getActiveRoles: () =>
    http.get<ActiveRolesResponse>('/roles/active'),

  getMyPermissions: () =>
    http.get<MyPermissionsResponse>('/roles/my-permissions'),

  getRoleById: (id: string) =>
    http.get<{ success: boolean; role: Role }>(`/roles/${id}`),

  createRole: (data: RoleFormData) =>
    http.post<{ success: boolean; role: Role }>('/roles', data),

  updateRole: (id: string, data: Partial<RoleFormData>) =>
    http.put<{ success: boolean; role: Role }>(`/roles/${id}`, data),

  updateRolePermissions: (id: string, permissions: string[]) =>
    http.put<{ success: boolean; role: Role }>(`/roles/${id}/permissions`, { permissions }),

  deleteRole: (id: string) =>
    http.delete<{ success: boolean; message: string }>(`/roles/${id}`),

  // Employment Roles
  getEmploymentRoles: (params?: { page?: number; limit?: number; search?: string; isActive?: string }) =>
    http.get<EmploymentRoleListResponse>('/employment-roles', { params }),

  getActiveEmploymentRoles: () =>
    http.get<{ success: boolean; employmentRoles: EmploymentRole[] }>('/employment-roles/active'),

  getEmploymentRoleById: (id: string) =>
    http.get<{ success: boolean; employmentRole: EmploymentRole }>(`/employment-roles/${id}`),

  createEmploymentRole: (data: EmploymentRoleFormData) =>
    http.post<{ success: boolean; employmentRole: EmploymentRole }>('/employment-roles', data),

  updateEmploymentRole: (id: string, data: Partial<EmploymentRoleFormData>) =>
    http.put<{ success: boolean; employmentRole: EmploymentRole }>(`/employment-roles/${id}`, data),

  deleteEmploymentRole: (id: string) =>
    http.delete<{ success: boolean; message: string }>(`/employment-roles/${id}`),

  // Employment Role Rates (merged report type + salary rule)
  getEmploymentRoleRates: (id: string) =>
    http.get<{ success: boolean; rates: EmploymentRoleRate[] }>(`/employment-roles/${id}/rates`),

  createEmploymentRoleRate: (id: string, data: EmploymentRoleRateFormData) =>
    http.post<{ success: boolean; rate: EmploymentRoleRate }>(`/employment-roles/${id}/rates`, data),

  updateEmploymentRoleRate: (id: string, rateId: string, data: { ratePerUnit?: number; fieldOverrides?: string | null }) =>
    http.put<{ success: boolean; rate: EmploymentRoleRate }>(`/employment-roles/${id}/rates/${rateId}`, data),

  deleteEmploymentRoleRate: (id: string, rateId: string) =>
    http.delete<{ success: boolean; message: string }>(`/employment-roles/${id}/rates/${rateId}`),

  // Employment Role Hospital Bonuses
  getHospitalBonuses: (roleId: string, rateId: string) =>
    http.get<{ success: boolean; bonuses: EmploymentRoleHospitalBonus[] }>(`/employment-roles/${roleId}/rates/${rateId}/hospital-bonuses`),

  createHospitalBonus: (roleId: string, rateId: string, data: EmploymentRoleHospitalBonusFormData) =>
    http.post<{ success: boolean; bonus: EmploymentRoleHospitalBonus }>(`/employment-roles/${roleId}/rates/${rateId}/hospital-bonuses`, data),

  updateHospitalBonus: (roleId: string, rateId: string, bonusId: string, data: { bonusAmount: number }) =>
    http.put<{ success: boolean; bonus: EmploymentRoleHospitalBonus }>(`/employment-roles/${roleId}/rates/${rateId}/hospital-bonuses/${bonusId}`, data),

  deleteHospitalBonus: (roleId: string, rateId: string, bonusId: string) =>
    http.delete<{ success: boolean; message: string }>(`/employment-roles/${roleId}/rates/${rateId}/hospital-bonuses/${bonusId}`),

  // Supervision Bonuses
  getSupervisionBonuses: (roleId: string, rateId: string) =>
    http.get<{ success: boolean; bonuses: SupervisionBonusConfig[] }>(`/employment-roles/${roleId}/rates/${rateId}/supervision-bonuses`),

  createSupervisionBonus: (roleId: string, rateId: string, data: SupervisionBonusFormData) =>
    http.post<{ success: boolean; bonus: SupervisionBonusConfig }>(`/employment-roles/${roleId}/rates/${rateId}/supervision-bonuses`, data),

  updateSupervisionBonus: (roleId: string, rateId: string, bonusId: string, data: Partial<SupervisionBonusFormData>) =>
    http.put<{ success: boolean; bonus: SupervisionBonusConfig }>(`/employment-roles/${roleId}/rates/${rateId}/supervision-bonuses/${bonusId}`, data),

  deleteSupervisionBonus: (roleId: string, rateId: string, bonusId: string) =>
    http.delete<{ success: boolean; message: string }>(`/employment-roles/${roleId}/rates/${rateId}/supervision-bonuses/${bonusId}`),

  // Employment Role Allowances
  getEmploymentRoleAllowances: (employmentRoleId: string) =>
    http.get<{ success: boolean; allowances: EmploymentRoleAllowance[] }>(`/employment-roles/${employmentRoleId}/allowances`),

  createEmploymentRoleAllowance: (employmentRoleId: string, data: EmploymentRoleAllowanceFormData) =>
    http.post<{ success: boolean; allowance: EmploymentRoleAllowance }>(`/employment-roles/${employmentRoleId}/allowances`, data),

  updateEmploymentRoleAllowance: (employmentRoleId: string, allowanceId: string, data: Partial<EmploymentRoleAllowanceFormData>) =>
    http.put<{ success: boolean; allowance: EmploymentRoleAllowance }>(`/employment-roles/${employmentRoleId}/allowances/${allowanceId}`, data),

  deleteEmploymentRoleAllowance: (employmentRoleId: string, allowanceId: string) =>
    http.delete<{ success: boolean; message: string }>(`/employment-roles/${employmentRoleId}/allowances/${allowanceId}`),

  // Employment Role Hospital Subset
  getHospitalSubset: (id: string) =>
    http.get<{ success: boolean; hospitalSubset: EmploymentRoleHospitalSubset[] }>(`/employment-roles/${id}/hospital-subset`),

  updateHospitalSubset: (id: string, hospitalIds: string[]) =>
    http.put<{ success: boolean; hospitalSubset: EmploymentRoleHospitalSubset[] }>(`/employment-roles/${id}/hospital-subset`, { hospitalIds }),

  syncHospitals: (id: string) =>
    http.post<{ success: boolean; message: string }>(`/employment-roles/${id}/sync-hospitals`),

  // Allowance Types
  getAllowanceTypes: (params?: { page?: number; limit?: number; search?: string; isActive?: string }) =>
    http.get<AllowanceTypeListResponse>('/allowance-types', { params }),

  getActiveAllowanceTypes: () =>
    http.get<{ success: boolean; allowanceTypes: AllowanceType[] }>('/allowance-types/active'),

  getAllowanceTypeById: (id: string) =>
    http.get<{ success: boolean; allowanceType: AllowanceType }>(`/allowance-types/${id}`),

  createAllowanceType: (data: AllowanceTypeFormData) =>
    http.post<{ success: boolean; allowanceType: AllowanceType }>('/allowance-types', data),

  updateAllowanceType: (id: string, data: Partial<AllowanceTypeFormData>) =>
    http.put<{ success: boolean; allowanceType: AllowanceType }>(`/allowance-types/${id}`, data),

  deleteAllowanceType: (id: string) =>
    http.delete<{ success: boolean; message: string }>(`/allowance-types/${id}`),

  // Currencies
  getCurrencies: (params?: { page?: number; limit?: number; search?: string; isActive?: string }) =>
    http.get<CurrencyListResponse>('/currencies', { params }),

  getActiveCurrencies: () =>
    http.get<{ success: boolean; currencies: Currency[] }>('/currencies/active'),

  getCurrencyById: (id: string) =>
    http.get<{ success: boolean; currency: Currency }>(`/currencies/${id}`),

  createCurrency: (data: CurrencyFormData) =>
    http.post<{ success: boolean; currency: Currency }>('/currencies', data),

  updateCurrency: (id: string, data: Partial<CurrencyFormData>) =>
    http.put<{ success: boolean; currency: Currency }>(`/currencies/${id}`, data),

  deleteCurrency: (id: string) =>
    http.delete<{ success: boolean; message: string }>(`/currencies/${id}`),

  // Export Presets
  getExportPresets: () =>
    http.get<{ success: boolean; presets: ExportPreset[] }>('/admin/export-presets'),

  createExportPreset: (data: ExportPresetFormData) =>
    http.post<{ success: boolean; preset: ExportPreset }>('/admin/export-presets', data),

  updateExportPreset: (id: string, data: Partial<ExportPresetFormData>) =>
    http.put<{ success: boolean; preset: ExportPreset }>(`/admin/export-presets/${id}`, data),

  deleteExportPreset: (id: string) =>
    http.delete<{ success: boolean }>(`/admin/export-presets/${id}`),

  setExportPresetDefault: (id: string) =>
    http.put<{ success: boolean; preset: ExportPreset }>(`/admin/export-presets/${id}/default`),
};
